/**
 * Names: the ones already in play, and one more when you need it.
 *
 * Replaces a canon panel a GM told us plainly they were not reading during a
 * session. What they *do* need, constantly, is a name — for the person they
 * mentioned twenty minutes ago and can no longer recall, and for the barkeep
 * nobody planned who a player just addressed.
 *
 * Both halves answer instantly. The first is canon the app already had. The
 * second filters a pool generated once for this campaign, so picking
 * "old, poor, feminine" is three taps rather than a model round-trip with a
 * table watching.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { GlanceEntity, PoolName } from '@/api/client';
import { getNames, markNameUsed } from '@/api/client';

type Gender = PoolName['gender'] | 'any';
type Age = PoolName['age'] | 'any';
type Standing = PoolName['standing'] | 'any';

const GENDERS: { value: Gender; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'masc', label: 'Masc' },
  { value: 'fem', label: 'Fem' },
  { value: 'neutral', label: 'Neutral' },
];
const AGES: { value: Age; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'young', label: 'Young' },
  { value: 'adult', label: 'Adult' },
  { value: 'old', label: 'Old' },
];
const STANDINGS: { value: Standing; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'poor', label: 'Poor' },
  { value: 'common', label: 'Common' },
  { value: 'rich', label: 'Rich' },
];

export function NamesPanel({
  campaignId,
  entities,
  previewPool = [],
}: {
  campaignId: string | null;
  entities: GlanceEntity[];
  /** A local pool for rehearsals and previews. It never calls the API or marks
   *  a name used, but otherwise exercises the real panel. */
  previewPool?: PoolName[];
}) {
  const hasPreview = previewPool.length > 0;
  const [pool, setPool] = useState<PoolName[]>(previewPool);
  const [status, setStatus] = useState<'pending' | 'ready' | 'failed'>(
    hasPreview ? 'ready' : 'pending',
  );
  const [gender, setGender] = useState<Gender>('any');
  const [age, setAge] = useState<Age>('any');
  const [standing, setStanding] = useState<Standing>('any');
  const [pick, setPick] = useState<PoolName | null>(null);
  const [taken, setTaken] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!campaignId || hasPreview) return;
    try {
      const result = await getNames(campaignId);
      setStatus(result.status);
      setPool(result.names);
    } catch {
      /* the panel is optional; never take the session view down */
    }
  }, [campaignId, hasPreview]);

  useEffect(() => {
    void load();
  }, [load]);

  // While the pool is being built, check back — but slowly. This is warmed at
  // session start, so a GM almost never sees the pending state at all.
  useEffect(() => {
    if (status !== 'pending' || !campaignId) return;
    const timer = window.setInterval(() => void load(), 8_000);
    return () => window.clearInterval(timer);
  }, [status, campaignId, load]);

  const matching = useMemo(
    () =>
      pool.filter(
        (n) =>
          !taken.includes(n.name) &&
          (gender === 'any' || n.gender === gender) &&
          (age === 'any' || n.age === age) &&
          (standing === 'any' || n.standing === standing),
      ),
    [pool, taken, gender, age, standing],
  );

  const roll = useCallback(() => {
    if (matching.length === 0) {
      setPick(null);
      return;
    }
    // Never the same one twice in a row unless it is the only one left.
    const options = matching.filter((n) => n.name !== pick?.name);
    const from = options.length ? options : matching;
    setPick(from[Math.floor(Math.random() * from.length)] ?? null);
  }, [matching, pick]);

  // A changed toggle should show its answer without a second tap.
  useEffect(() => {
    if (pool.length) roll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, age, standing, pool.length]);

  const people = entities.filter((e) => e.kind === 'person' || e.kind === 'faction');

  function take() {
    if (!pick) return;
    void navigator.clipboard?.writeText(pick.name);
    if (campaignId) void markNameUsed(campaignId, pick.name);
    setTaken((t) => [...t, pick.name]);
    roll();
  }

  return (
    <div className="pane pane--names">
      <header className="pane__header">
        <h2 className="pane__title">Names</h2>
      </header>
      <div className="pane__body names">
        {/* No "In play" heading: the pane is already called Names, and two
            headings stacked above one list is a row of pixels spent saying
            the same thing twice in a column that has none to spare. */}
        <section className="names__block">
          {people.length === 0 ? (
            <p className="names__empty">
              Names appear here as they come up at the table.
            </p>
          ) : (
            <div className="names__chips-scroll">
              <ul className="names__chips">
                {people.map((e) => (
                  <li key={e.id} className="names__chip" title={e.one_line}>
                    {e.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* A disclosure, not a permanent block. Open, this half is three rows
            of toggles and a result — and measured in the live sidebar the
            whole panel was taking 248 of 427 pixels, which is why the pane
            below it had two lines to read replies in. The names already in
            play stay visible; generating a new one is a tap. */}
        <details className="names__gen">
          <summary className="names__heading names__gen-open">Need a name</summary>
          <Toggle options={GENDERS} value={gender} onChange={setGender} label="Gender" />
          <Toggle options={AGES} value={age} onChange={setAge} label="Age" />
          <Toggle
            options={STANDINGS}
            value={standing}
            onChange={setStanding}
            label="Standing"
          />

          {status === 'pending' && pool.length === 0 ? (
            <p className="names__empty">
              <span className="spinner" aria-hidden="true" /> Building names that
              fit this world…
            </p>
          ) : pick ? (
            <div className="names__pick">
              <strong className="names__picked">{pick.name}</strong>
              {pick.note ? <span className="names__note">{pick.note}</span> : null}
              <div className="names__actions">
                <button type="button" className="button button--small" onClick={take}>
                  Use it
                </button>
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={roll}
                >
                  Another
                </button>
              </div>
            </div>
          ) : (
            <p className="names__empty">
              Nothing left with those settings. Try widening one.
            </p>
          )}
        </details>
      </div>
    </div>
  );
}

function Toggle<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="names__toggle" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={
            option.value === value
              ? 'names__opt names__opt--on'
              : 'names__opt'
          }
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
